import { Card } from "@/ui/components/primitives/card";
import { Skeleton } from "@/ui/components/primitives/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/ui/components/primitives/tabs";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { VolumeChartPoint } from "@/services/api/services/merchant-dashboard";
import { useState } from "react";

interface VolumeChartProps {
  weekData: VolumeChartPoint[];
  monthData: VolumeChartPoint[];
  isLoading?: boolean;
}

export function VolumeChart({ weekData, monthData, isLoading }: VolumeChartProps) {
  const [period, setPeriod] = useState<"week" | "month">("week");
  const data = period === "week" ? weekData : monthData;

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="mb-6">
          <div className="h-6 w-40 bg-muted rounded animate-pulse" />
          <div className="h-4 w-48 bg-muted rounded animate-pulse mt-2" />
        </div>
        <div className="mt-4 w-full">
        <Skeleton className="h-[320px] w-full" />
      </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold">Volume Overview</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Payment inflow and outflow trends
          </p>
        </div>
        <div className="mt-4 w-full">
        <div className="h-[320px] flex items-center justify-center text-muted-foreground">
          No data available for the selected period
        </div>
      </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Volume Overview</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Payment inflow and outflow trends
            </p>
          </div>
          <Tabs value={period} onValueChange={(value) => setPeriod(value as "week" | "month")}>
            <TabsList>
              <TabsTrigger value="week">Week</TabsTrigger>
              <TabsTrigger value="month">Month</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      <div className="mt-4 w-full overflow-x-auto">
      <ResponsiveContainer width="100%" height={320} minWidth={300}>
        <AreaChart
          data={data}
          margin={{ top: 20, right: 10, left: 10, bottom: 40 }}
        >
          <defs>
            <linearGradient id="payinGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="payoutGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="date"
            className="text-xs"
            height={60}
            tick={(props) => {
              const { x, y, payload, index } = props;
              const isMobile = window.innerWidth < 640;
              // Show every other label on mobile to prevent overlap
              if (isMobile && index % 2 !== 0) return null;

              return (
                <text
                  x={x}
                  y={y + 10}
                  fill="hsl(var(--foreground))"
                  fontSize={isMobile ? 10 : 12}
                  textAnchor="middle"
                  transform={`rotate(${isMobile ? -45 : 0}, ${x}, ${y + 10})`}
                >
                  {payload.value}
                </text>
              );
            }}
          />
          <YAxis
            className="text-xs"
            width={60}
            tick={(props) => {
              const { x, y, payload } = props;
              const isMobile = window.innerWidth < 640;
              return (
                <text
                  x={x - 5}
                  y={y}
                  fill="hsl(var(--foreground))"
                  fontSize={isMobile ? 10 : 12}
                  textAnchor="end"
                >
                  {payload.value}
                </text>
              );
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
            }}
            formatter={(value: number, name: string) => [
              `₹${value.toLocaleString()}`,
              name === "payin" ? "Payin" : "Payout"
            ]}
          />
          <Area
            type="monotone"
            dataKey="payin"
            stroke="#a855f7"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#payinGradient)"
            name="payin"
          />
          <Area
            type="monotone"
            dataKey="payout"
            stroke="#22c55e"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#payoutGradient)"
            name="payout"
          />
        </AreaChart>
      </ResponsiveContainer>
      </div>
    </Card>
  );
}